import React, { useCallback, useReducer, useContext, useEffect } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IUpdateRestaurantPropertiesAsPatchForSubcategory } from 'types';
import { SaveAsPatchActionTypes } from './saveAsPatchModalActions';
import { saveAsPatchReducer } from './saveAsPatchModalReducer';
import { saveAsPatchInitialState, SaveAsPatchContext, ISaveAsPatchState } from './saveAsPatchModalContext';
import {
	IEditSubcategoryRestaurantPropertiesContext,
	EditSubcategoryRestaurantPropertiesContext,
} from '../../../../editSubcategoryRestaurantPropertiesState/editSubcategoryRestaurantPropertiesContext';
import { convertUndefinedFields } from 'utils/helpers';
import { updateSubcategoryRestaurantPropertiesAsPatch } from 'backend/subcategories';

export const SaveAsPatchState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(saveAsPatchReducer, saveAsPatchInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		refetchRestPropsData,
		changeTypeOfModalWindow: changeTypeOfParentModalWindow,
		subcategoryId,
		customerName,
		categoryName,
		fetchedRestaurants,
		IDSelectedRestaurant,
		hasSchedulePriority,
		activationSchedule,
		deactivationSchedule,
		availableForRepetition,
		isHidden,
	} = useContext<IEditSubcategoryRestaurantPropertiesContext>(EditSubcategoryRestaurantPropertiesContext);

	useEffect(() => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_INITIAL_FORM_DATA,
			payload: {
				initialFormData: {
					categoryId: subcategoryId,
					customerName,
					categoryName,
					fetchedRestaurants,
					IDSelectedRestaurantOnRestPropsPage: IDSelectedRestaurant,
					hasSchedulePriority,
					activationSchedule,
					deactivationSchedule,
					availableForRepetition,
					isHidden,
				},
			},
		});
	}, []);

	const saveData = useCallback(
		async (value: 'saveAndMakeOtherChanges' | 'saveAndClose') => {
			try {
				const dataForSubmit: IUpdateRestaurantPropertiesAsPatchForSubcategory = {
					subCategoryId: subcategoryId,
					restaurantIds:
						state.typeOfChanges === 'basedChanges'
							? [
									state.initialFormData.IDSelectedRestaurantOnRestPropsPage || '',
									...state.IDsSelectedRestaurant,
							  ]
							: state.IDsSelectedRestaurant,
					update: {
						activationCron: {
							shouldUpdate: state.selectedProperties.includes('activation'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.activationSchedule
									: state.activationSchedule,
						},
						deactivationCron: {
							shouldUpdate: state.selectedProperties.includes('deactivation'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.deactivationSchedule
									: state.deactivationSchedule,
						},
						availableForRepetition: {
							shouldUpdate: state.selectedProperties.includes('availableForRepetition'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.availableForRepetition
									: state.availableForRepetition,
						},
						isHidden: {
							shouldUpdate: state.selectedProperties.includes('hideSubcategory'),
							value:
								state.typeOfChanges === 'basedChanges'
									? state.initialFormData.isHidden
									: state.isHidden,
						},
					},
				};
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateSubcategoryRestaurantPropertiesAsPatch(convertedData);
				if (value === 'saveAndClose') {
					changeTypeOfParentModalWindow(null);
					refetchRestPropsData(true);
				}
				if (value === 'saveAndMakeOtherChanges') {
					dispatch({ type: SaveAsPatchActionTypes.RESET_FIELDS });
				}
			} catch (err) {
				dispatch({
					type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'error',
					},
				});
			} finally {
				hideLoader();
			}
		},
		[showLoader, hideLoader, state],
	);

	const setTypeOfChanges = useCallback(
		(value: ISaveAsPatchState['typeOfChanges']) => {
			dispatch({
				type: SaveAsPatchActionTypes.SET_TYPE_OF_CHANGES,
				payload: {
					typeOfChanges: value,
					IDsSelectedRestaurant:
						value === 'basedChanges'
							? state.IDsSelectedRestaurant.filter(
									(id) => id !== state.initialFormData.IDSelectedRestaurantOnRestPropsPage,
							  )
							: state.IDsSelectedRestaurant,
				},
			});
		},
		[state.IDsSelectedRestaurant, state.initialFormData.IDSelectedRestaurantOnRestPropsPage],
	);

	const setIDsRestaurant = useCallback((value: ISaveAsPatchState['IDsSelectedRestaurant']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setProperties = useCallback((value: ISaveAsPatchState['selectedProperties']) => {
		dispatch({
			type: SaveAsPatchActionTypes.SET_PROPERTIES,
			payload: {
				selectedProperties: value,
			},
		});
	}, []);

	const changeActivationSchedule = useCallback((value: ISaveAsPatchState['activationSchedule']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: ISaveAsPatchState['deactivationSchedule']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const toggleAvailableForRepetition = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_AVAILABLE_FOR_REPETITION,
		});
	}, []);

	const toggleIsHidden = useCallback(() => {
		dispatch({
			type: SaveAsPatchActionTypes.TOGGLE_IS_HIDDEN,
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: ISaveAsPatchState['typeOfModalWindow']) => {
		dispatch({
			type: SaveAsPatchActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<SaveAsPatchContext.Provider
			value={{
				saveData,
				setTypeOfChanges,
				setIDsRestaurant,
				setProperties,
				changeActivationSchedule,
				changeDeactivationSchedule,
				toggleAvailableForRepetition,
				toggleIsHidden,
				changeTypeOfModalWindow,
				initialFormData: state.initialFormData,
				typeOfChanges: state.typeOfChanges,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				selectedProperties: state.selectedProperties,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				availableForRepetition: state.availableForRepetition,
				isHidden: state.isHidden,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</SaveAsPatchContext.Provider>
	);
};
